import { useMutation, useQuery } from '@tanstack/react-query';
import { getTranslatedString } from 'helpers/i18n';
import React, { useContext, useState } from 'react';
import {
  AntdPageHeader,
  ApplicationUIContext,
  ButtonGroup,
  CardTitle,
  ContentWrapper,
  Form,
} from 'ui-library';
import { IncentiveStatusTag, incentiveStatus } from './common/IncentiveStatus';
import { apiGet, apiPost, apiPutRequest } from 'helpers/ApiHelper';
import IncentiveForm from './IncentiveForm';
import { Card, Divider, message, Spin } from 'antd';
import moment from 'moment';
import { appendUnitIds, getSuccessfulMessage, formatPayload } from './common/helpers';
import { onError } from 'components/landlord_app/application_management/common/ErrorHandling';

const formatAssociationGroups = (associationGroups) =>
  associationGroups.map((associationGroup) => ({
    ...associationGroup,
    valid_from: moment(associationGroup.valid_from),
    valid_to: associationGroup.valid_to ? moment(associationGroup.valid_to) : null,
    wont_expire: !associationGroup.valid_to,
    associations: appendUnitIds(associationGroup.unit_ids),
  }));

const formatIncentiveCodes = (incentiveCodes) =>
  incentiveCodes.map((incentiveCode) => ({
    ...incentiveCode,
    valid_from: moment(incentiveCode.valid_from),
    valid_to: incentiveCode.valid_to ? moment(incentiveCode.valid_to) : null,
    wont_expire: !incentiveCode.valid_to,
  }));

const formatBenefits = (benefits) =>
  benefits.map((benefit) => ({
    ...benefit,
    amount_per_month: benefit?.amount_per_month_cents / 100,
  }));

const formatResponse = (response) => ({
  ...response?.incentive_template,
  association_groups: formatAssociationGroups(response?.incentive_template?.association_groups),
  benefits: formatBenefits(response?.incentive_template?.benefits),
  incentive_codes: formatIncentiveCodes(response?.incentive_template?.incentive_codes),
});

const EditIncentive = ({ router, routeParams }) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const [editIncentiveForm] = Form.useForm();
  const [isError, setIsError] = useState(false);
  const status = Form.useWatch('status', editIncentiveForm);

  const {
    data: incentive,
    isLoading,
    refetch: refetchIncentive,
  } = useQuery({
    queryKey: ['incentives', routeParams?.incentiveId],
    queryFn: () =>
      apiGet(`/landlords/incentives/incentive_templates/${routeParams?.incentiveId}`).then(
        formatResponse
      ),
  });

  const editIncentiveMutation = useMutation({
    mutationFn: (data) =>
      apiPutRequest(
        `/landlords/incentives/incentive_templates/${routeParams?.incentiveId}`,
        formatPayload(data)
      ),
    onSuccess: () => {
      message.success(getSuccessfulMessage(status));
      refetchIncentive();
    },
    onError,
  });

  const duplicateIncentiveMutation = useMutation({
    mutationFn: (incentiveTemplateId) =>
      apiPost(`/landlords/incentives/incentive_templates/${incentiveTemplateId}/clone`),
    onSuccess: (response) => router.push(`/companies/incentives/${response.incentive_template.id}`),
    onError,
  });

  const onSaveDraft = () => {
    editIncentiveForm.setFieldsValue({ status: incentiveStatus.draft });
    editIncentiveForm.submit();
  };

  const onPublish = () => {
    editIncentiveForm.setFieldsValue({ status: incentiveStatus.published });
    editIncentiveForm.submit();
  };

  const onArchive = () => {
    editIncentiveForm.setFieldsValue({ status: incentiveStatus.archived });
    editIncentiveForm.submit();
  };

  const onClone = () => {
    duplicateIncentiveMutation.mutate(routeParams?.incentiveId);
  };

  const handleFinish = (data) => {
    editIncentiveMutation.mutate(data);
    setIsError(false);
  };

  const handleFinishFailed = () => {
    setIsError(true);
  };

  const isDraft = incentive?.status === incentiveStatus.draft;
  const isPublished = incentive?.status === incentiveStatus.published;
  const isArchived = incentive?.status === incentiveStatus.archived;

  const canArchive = isDraft || isPublished;
  const canClone = isPublished || isArchived;

  return (
    <div className="body" role="main">
      <AntdPageHeader
        onBack={() => router.push('/companies/incentives')}
        title={incentive?.internal_name || ''}
        extra={
          <ButtonGroup
            buttons={[
              isDraft && {
                label: getTranslatedString('landlordPortal.incentives.saveAsDraft'),
                onClick: onSaveDraft,
                type: 'secondary',
                loading: editIncentiveMutation.isLoading && isDraft,
              },
              isDraft && {
                label: getTranslatedString('landlordPortal.incentives.publish'),
                onClick: onPublish,
                type: 'primary',
                loading: editIncentiveMutation.isLoading,
              },
              canArchive && {
                label: getTranslatedString('landlordPortal.incentives.archive'),
                onClick: onArchive,
                type: 'secondary',
                loading: editIncentiveMutation.isLoading,
              },
              canClone && {
                label: getTranslatedString('landlordPortal.incentives.duplicate'),
                onClick: onClone,
                type: 'secondary',
                loading: duplicateIncentiveMutation.isLoading,
              },
            ].filter(Boolean)}
            isMobile={isMobile}
          />
        }
        tags={[IncentiveStatusTag(incentive)]}
        isMobile={isMobile}
      />
      <ContentWrapper>
        <Card
          style={{ maxWidth: 584 }}
          bodyStyle={{ padding: isMobile && 16, fontSize: isMobile && 16 }}
        >
          <CardTitle>{getTranslatedString('landlordPortal.incentives.configuration')}</CardTitle>

          <Divider />

          {isLoading ? <Spin /> : null}

          {!isLoading ? (
            <IncentiveForm
              form={editIncentiveForm}
              onFinish={handleFinish}
              onFinishFailed={handleFinishFailed}
              isError={isError}
              initialValues={incentive}
              disabled={isPublished || isArchived}
            />
          ) : null}
        </Card>
      </ContentWrapper>
    </div>
  );
};

export default EditIncentive;
